import React, { useMemo, useState } from 'react';

import { Link as RouterLink } from 'react-router-dom';
import { lensTokens } from '~/configs/unifiedAnalyticsTheme';

import {
  Autocomplete,
  Box,
  Chip,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import ProductPerformanceTrend from '../ProductPerformance/ProductPerformanceTrend';

import { barOption } from './charts';
import {
  ACTIVITY_ORDER,
  type Business,
  SAVED_INDUSTRIES,
  activityCounts,
  adminBusinessPath,
  registrations,
  registrationsByMonth,
  sydneyToday,
} from './model';

type Props = { businesses: Array<Business>; source: string };

type AccountFilter = 'activity' | 'industry' | 'onboarding';
const ACCOUNT_FILTERS: ReadonlyArray<{ key: AccountFilter; label: string }> = [
  { key: 'activity', label: 'Activity status' },
  { key: 'industry', label: 'Industry' },
  { key: 'onboarding', label: 'Onboarding status' },
];

export default function LebtCommercialBusiness({ businesses, source }: Props) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const today = sydneyToday();
  const [selected, setSelected] = useState<Record<AccountFilter, Array<string>>>({
    activity: [],
    industry: SAVED_INDUSTRIES,
    onboarding: [],
  });
  // As in the workbook, the account filters narrow the activity chart and accounts list, not registrations,
  // and Industry starts on the workbook's saved list.
  const accounts = useMemo(
    () =>
      businesses.filter((business) =>
        ACCOUNT_FILTERS.every(({ key }) => !selected[key].length || selected[key].includes(business[key])),
      ),
    [businesses, selected],
  );
  const counts = registrations(businesses, today);
  const byMonth = registrationsByMonth(businesses);
  const activity = activityCounts(accounts);
  const asOf = businesses.find((business) => business.asOf)?.asOf ?? today;
  const sorted = [...accounts].sort(
    (a, b) => ACTIVITY_ORDER.indexOf(a.activity) - ACTIVITY_ORDER.indexOf(b.activity) || b.orders - a.orders,
  );

  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
          gap: 1.5,
        }}
      >
        {(
          [
            ['L7D Registrations', counts.l7d],
            ['L30D Registrations', counts.l30d],
            ['MTD Registrations', counts.mtd],
            ['YTD Registrations', counts.ytd],
          ] as const
        ).map(([label, value]) => (
          <Paper key={label} variant="outlined" sx={{ p: 1.5, bgcolor: tokens.surface }}>
            <Typography variant="caption" color="text.secondary">
              {label}
            </Typography>
            <Typography variant="h5" sx={{ color: tokens.accent, fontVariantNumeric: 'tabular-nums' }}>
              {value.toLocaleString('en-AU')}
            </Typography>
          </Paper>
        ))}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '5fr 6fr' }, gap: 2, minWidth: 0 }}>
        <ProductPerformanceTrend
          title="Registrations Chart"
          option={barOption(
            byMonth.map((entry) => entry.month),
            byMonth.map((entry) => entry.count),
            'New businesses',
            tokens.accent,
          )}
          source={source}
          range={`Registration month, as at ${asOf}`}
          height={280}
        />
        <ProductPerformanceTrend
          title="Activity Chart"
          option={barOption(
            activity.map((entry) => entry.status),
            activity.map((entry) => entry.count),
            'Businesses',
            tokens.accent,
          )}
          source={source}
          range={`Activity status as at ${asOf}`}
          height={280}
        />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5 }}>
        {ACCOUNT_FILTERS.map(({ key, label }) => (
          <Autocomplete
            key={key}
            multiple
            size="small"
            options={[...new Set(businesses.map((business) => business[key]))].sort()}
            value={selected[key]}
            onChange={(_, values) => setSelected((current) => ({ ...current, [key]: values }))}
            renderTags={(values, getTagProps) =>
              values.map((option, index) => {
                const { key: tagKey, ...tagProps } = getTagProps({ index });
                return <Chip key={tagKey} {...tagProps} label={option} size="small" />;
              })
            }
            renderInput={(params) => <TextField {...params} label={label} placeholder="All" />}
          />
        ))}
      </Box>
      <Paper variant="outlined" sx={{ minWidth: 0 }}>
        <Typography component="h2" variant="h6" sx={{ px: 2, py: 1.5, fontSize: 17, bgcolor: tokens.plateHover }}>
          Accounts Summary · {accounts.length.toLocaleString('en-AU')} businesses
        </Typography>
        <TableContainer sx={{ maxHeight: 560 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Business name</TableCell>
                <TableCell>Joined</TableCell>
                <TableCell>Last transaction</TableCell>
                <TableCell>Activity status</TableCell>
                <TableCell align="right">Orders</TableCell>
                <TableCell>Admin</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((business) => (
                <TableRow key={business.id} hover>
                  <TableCell>{business.name}</TableCell>
                  <TableCell>{business.joined ?? '—'}</TableCell>
                  <TableCell>{business.lastTransaction ?? '—'}</TableCell>
                  <TableCell>{business.activity}</TableCell>
                  <TableCell align="right">{business.orders.toLocaleString('en-AU')}</TableCell>
                  <TableCell>
                    <Link component={RouterLink} to={adminBusinessPath(business.id)}>
                      Go to Admin page
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2, py: 1 }}>
          {source} · activity status as at {asOf} · registrations counted to {today} (Sydney)
        </Typography>
      </Paper>
    </Box>
  );
}
